import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, CartViewEvent, StandardEvents } from '@shopify/events';

const SECTION = 'cart-drawer-section';

/** Modal presentation around Shopify's native cart lines and section rendering. */
class FivePointCart extends Component {
  requiredRefs = ['panel'];
  #previousFocus;
  #closing;
  #busy = false;
  #pendingAdd = false;
  #unconfirmed = false;
  #nativeUpdates = 0;
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.refs.panel.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
    this.refs.panel.addEventListener('click', event => {
      if (event.target !== this.refs.panel) return;
      const box = this.refs.panel.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
    }, options);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onUpdate, options);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#closing?.cancel();
    unlockScroll(this.refs.panel);
  }

  #setBusy() {
    const busy = this.#busy || this.#nativeUpdates > 0;
    const locked = busy || this.#unconfirmed;
    this.setAttribute('aria-busy', String(busy));
    this.toggleAttribute('data-fp-bag-adding', this.#pendingAdd);
    this.toggleAttribute('data-fp-bag-unconfirmed', this.#unconfirmed);
    const pending = this.querySelector('[data-fp-bag-pending]');
    if (pending) pending.hidden = !this.#pendingAdd;
    const content = this.querySelector('cart-items-component');
    if (content) content.inert = locked;
    this.querySelectorAll('[data-fp-bag-checkout],[data-fp-bag-clear]').forEach(button => { button.disabled = locked; });
  }

  #error(message) {
    this.querySelector('[data-fp-bag-error-text]').textContent = message;
    this.querySelector('[data-fp-bag-error]').hidden = !message;
  }

  open() {
    const panel = this.refs.panel;
    this.#closing?.cancel();
    this.#closing = null;
    if (panel.open) return;
    this.#previousFocus = document.activeElement;
    panel.showModal();
    lockScroll(panel);
    document.querySelectorAll('[aria-controls="cart-drawer"]').forEach(button => button.setAttribute('aria-expanded', 'true'));
    this.querySelector('.fp-bag__close')?.focus({ preventScroll: true });
  }

  close() {
    const panel = this.refs.panel;
    if (!panel.open || this.#closing) return;
    const finish = () => {
      panel.close();
      unlockScroll(panel);
      document.querySelectorAll('[aria-controls="cart-drawer"]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (this.#previousFocus?.isConnected) this.#previousFocus.focus({ preventScroll: true });
      this.#closing = null;
    };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { finish(); return; }
    this.#closing = panel.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }], { duration: 230, easing: 'ease-in', fill: 'forwards' });
    this.#closing.onfinish = () => { this.#closing.cancel(); finish(); };
  }

  toggle() { if (this.refs.panel.open) this.close(); else this.open(); }

  #onUpdate = event => {
    if (!event.promise) return;
    const sourceDialog = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    this.#nativeUpdates++;
    this.#setBusy();
    event.promise.then(({ detail }) => {
      this.#error('');
      // Our writer owns opening; do not reopen an early drawer the buyer closed.
      if (detail?.source === 'fivepoint-cart' || event.action !== 'add' || detail?.didError || !this.hasAttribute('auto-open')) return;
      const open = () => requestAnimationFrame(() => this.open());
      if (sourceDialog?.open && sourceDialog !== this.refs.panel) sourceDialog.addEventListener('close', open, { once: true });
      else open();
    }).catch(() => {
      this.#error(window.FivePointI18n.t("The cart update could not be confirmed. Review your cart before trying again."));
    }).finally(() => { this.#nativeUpdates--; this.#setBusy(); });
  };

  async #request(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, ...options, signal: AbortSignal.timeout(20000) });
    const data = await response.json();
    if (!response.ok || data.status) {
      const error = new Error(typeof data.description === 'string' ? data.description : window.FivePointI18n.t("The cart update could not be confirmed."));
      error.httpStatus = response.status;
      throw error;
    }
    return data;
  }

  #cartFromSection(sections) {
    const html = sections?.[SECTION];
    if (typeof html !== 'string') throw new Error(window.FivePointI18n.t("Your cart changed, but the shopping bag could not refresh."));
    const document = new DOMParser().parseFromString(html, 'text/html');
    const inner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');
    const component = inner?.querySelector('cart-items-component');
    const items = JSON.parse(inner?.getAttribute('data-fp-bag-items') || 'null');
    const payload = JSON.parse(component?.getAttribute('view-event-payload') || 'null');
    // Shopify's native view adapter resolves the cart identity from the current
    // session. Constructing this event does not dispatch an extra analytics event.
    const standardCart = payload?.cart ? new CartViewEvent(payload).cart : null;
    const money = value => typeof value?.amount === 'string' && /^\d+(\.\d+)?$/.test(value.amount) && /^[A-Z]{3}$/.test(value.currencyCode);
    const invalid = () => { throw new Error(window.FivePointI18n.t("The shopping bag response was incomplete. Review your cart before trying again.")); };
    if (!standardCart?.id || !Number.isSafeInteger(standardCart.totalQuantity) || standardCart.totalQuantity < 1 ||
      !money(standardCart.cost?.totalAmount) || !Array.isArray(standardCart.lines) || !Array.isArray(standardCart.discountCodes) ||
      standardCart.discountCodes.some(code => typeof code.code !== 'string' || typeof code.applicable !== 'boolean') ||
      !Array.isArray(items) || items.length !== standardCart.lines.length) invalid();
    const keys = new Set();
    for (const [index, item] of items.entries()) {
      const line = standardCart.lines[index];
      if (typeof item.key !== 'string' || !item.key || keys.has(item.key) || line.id !== item.key ||
        !Number.isSafeInteger(item.variant_id) || item.variant_id < 1 || !Number.isSafeInteger(item.product_id) || item.product_id < 1 ||
        !(item.variant_title === null || typeof item.variant_title === 'string') ||
        !Number.isSafeInteger(item.quantity) || item.quantity < 1 || line.quantity !== item.quantity ||
        !money(line.cost?.totalAmount) || line.cost.totalAmount.currencyCode !== standardCart.cost.totalAmount.currencyCode) invalid();
      keys.add(item.key);
    }
    if (items.reduce((sum, item) => sum + item.quantity, 0) !== standardCart.totalQuantity) invalid();
    return { cart: { items, item_count: standardCart.totalQuantity }, standardCart };
  }

  async #renderAndNotify(cart, sections, action, lines = [], standardCart = CartLinesUpdateEvent.createCartFromAjaxResponse(cart)) {
    if (typeof sections?.[SECTION] !== 'string' || !sections[SECTION].includes('data-hydration-key="cart-drawer-inner"')) throw new Error(window.FivePointI18n.t("Your cart changed, but the shopping bag could not refresh."));
    if (!Array.isArray(cart.items) || !Number.isFinite(cart.item_count)) throw new Error(window.FivePointI18n.t("The cart response was incomplete."));
    await morphSection(SECTION, sections[SECTION], { mode: 'hydration', injectStylesheet: true });
    const items = this.querySelector('cart-items-component');
    if (!items) throw new Error(window.FivePointI18n.t("The shopping bag could not refresh."));
    this.#unconfirmed = false;
    this.#setBusy();
    // Dispatch from the hydrated native component so it does not render twice.
    items.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: Promise.resolve({
      cart: standardCart,
      detail: { items: cart.items, sections, source: 'fivepoint-cart', didError: false },
    }) }));
    this.#error('');
  }

  async addItems(items, eventContext = {}, { openImmediately = false } = {}) {
    if (!Array.isArray(items) || !items.length || items.length > 40 || items.some(item => !/^\d+$/.test(String(item.id)) || item.quantity !== 1)) throw new Error(window.FivePointI18n.t("The selected colour cards are invalid."));
    return this.#add(items, eventContext, openImmediately);
  }

  /** One validated product choice; colour-card batching retains its quantity=1 contract. */
  async addProduct(id, quantity, eventContext = {}) {
    if (!/^[1-9]\d*$/.test(String(id)) || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 200) throw new Error(window.FivePointI18n.t("The selected product quantity is invalid."));
    return this.#add([{ id: String(id), quantity }], eventContext);
  }

  #sectionIds() {
    // Match the native quantity writer: the cart page and drawer share a response.
    // Pages without a cart-page component still request only the drawer.
    const sections = new Set([SECTION]);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(items => {
      if (items.dataset.sectionId) sections.add(items.dataset.sectionId);
    });
    return Array.from(sections);
  }

  async #add(items, eventContext, openImmediately = false) {
    if (this.#busy || this.#nativeUpdates || this.#unconfirmed) {
      const error = new Error(this.#unconfirmed ? window.FivePointI18n.t("Review your cart before adding again.") : window.FivePointI18n.t("Please wait for the current cart update."));
      error.cartResult = 'rejected';
      throw error;
    }
    this.#busy = true;
    this.#pendingAdd = openImmediately;
    this.#error('');
    this.#setBusy();
    let result = 'unknown', refresh = 'not_attempted';
    try {
      if (openImmediately) this.open();
      const added = await this.#request(this.dataset.addUrl, { method: 'POST', body: JSON.stringify({ items, sections: this.#sectionIds(), sections_url: location.pathname }) });
      if (!Array.isArray(added.items) || added.items.length !== items.length) throw new Error(window.FivePointI18n.t("The cart response was incomplete."));
      result = 'committed'; refresh = 'failed';
      const { cart, standardCart } = this.#cartFromSection(added.sections);
      await this.#renderAndNotify(cart, added.sections, 'add', items.map(item => ({ merchandiseId: item.id, quantity: item.quantity })), standardCart);
      refresh = 'success';
      if (!openImmediately) this.open();
      return cart;
    } catch (error) {
      error.cartResult = result === 'committed' ? 'committed' : error.httpStatus >= 400 && error.httpStatus < 500 ? 'rejected' : 'unknown';
      result = error.cartResult;
      if (openImmediately) {
        this.#unconfirmed = result !== 'rejected';
        this.#error(this.#unconfirmed ? window.FivePointI18n.t("The cart update could not be confirmed. Review your cart before trying again.") : error.message);
      }
      throw error;
    } finally {
      window.FivePointEvents?.emit('fp_cart_write_result', {result,refresh_result:refresh,item_count:items.length}, {source_surface:eventContext.source_surface || 'cart',component_id:eventContext.component_id || 'cart-batch-add',...(eventContext.page_type ? {page_type:eventContext.page_type} : {})});
      this.#pendingAdd = false;
      this.#busy = false; this.#setBusy();
    }
  }

  async refreshAndOpen() {
    if (this.#busy || this.#nativeUpdates) return;
    this.#busy = true;
    this.#setBusy();
    this.open();
    try {
      const url = new URL(location.href);
      url.searchParams.set('sections', this.#sectionIds().join(','));
      const [cart, sections] = await Promise.all([
        this.#request(`${this.dataset.cartUrl}.js`),
        // JSON negotiation on a product URL returns product data, not section HTML.
        this.#request(url, { headers: { Accept: 'text/html' } }),
      ]);
      await this.#renderAndNotify(cart, sections, 'update');
    } catch (error) { this.#error(error.message); }
    finally { this.#busy = false; this.#setBusy(); }
  }

  async clearCart() {
    if (this.#busy || this.#nativeUpdates || this.#unconfirmed) return;
    this.#busy = true;
    this.#setBusy();
    try {
      const cart = await this.#request(this.dataset.clearUrl, { method: 'POST', body: JSON.stringify({ sections: this.#sectionIds(), sections_url: location.pathname }) });
      await this.#renderAndNotify(cart, cart.sections, 'remove');
    } catch (error) { this.#error(window.FivePointI18n.t("{{p0}} Review your cart before trying again.", [error.message])); }
    finally { this.#busy = false; this.#setBusy(); }
  }
}

if (!customElements.get('fivepoint-cart-component')) customElements.define('fivepoint-cart-component', FivePointCart);
